import type { ElementType, ReactNode } from "react";
import styles from "./DocumentListRow.module.css";
// @ts-expect-error pure js
import { classnames } from "../../utils";
import { Button as BaseButton } from "@base-ui/react/button";
import Text from "../UI/Text";
import Tooltip from "../UI/Tooltip";

type Column = "status" | "share" | "date" | "actions";

const COLUMN_CLASS: Record<Column, string> = {
  status: styles.cellStatus,
  share: styles.cellShare,
  date: styles.cellDate,
  actions: styles.cellActions,
};

interface DocumentListRowProps {
  children: ReactNode;
  /** Ligne en tête de tableau (libellés de colonnes). */
  header?: boolean;
  selected?: boolean;
  /** Fichier d'un dossier déplié : en retrait, relié au dossier par un guide. */
  child?: boolean;
  /** Dernier fichier du dossier : le guide s'arrête à sa hauteur. */
  last?: boolean;
  /** Clic sur la ligne hors de ses contrôles (case, bascule, menu, boutons). */
  onClick?: () => void;
  className?: string;
}

const INTERACTIVE_SELECTOR = "button, a, input, label, [role='menu']";

/** Une ligne de la vue liste des documents ; la grille des colonnes est commune à toutes les lignes. */
export function DocumentListRow({
  children,
  header,
  selected,
  child,
  last,
  onClick,
  className,
}: DocumentListRowProps) {
  return (
    <div
      role="row"
      className={classnames(
        styles.row,
        header && styles.header,
        selected && styles.selected,
        child && styles.child,
        last && styles.last,
        onClick && styles.clickable,
        className,
      )}
      onClick={
        onClick
          ? (event) => {
              if ((event.target as HTMLElement).closest(INTERACTIVE_SELECTOR)) {
                return;
              }
              onClick();
            }
          : undefined
      }
    >
      {children}
    </div>
  );
}

interface DocumentListCellProps {
  column: Column;
  children?: ReactNode;
  header?: boolean;
  className?: string;
}

export function DocumentListCell({
  column,
  children,
  header,
  className,
}: DocumentListCellProps) {
  return (
    <div
      role={header ? "columnheader" : "cell"}
      className={classnames(styles.cell, COLUMN_CLASS[column], className)}
    >
      {children}
    </div>
  );
}

interface DocumentListSelectCellProps {
  checked: boolean;
  indeterminate?: boolean;
  label: string;
  onChange?: (checked: boolean) => void;
  /** Explique pourquoi la ligne n'est pas sélectionnable ; la case est alors désactivée. */
  disabledReason?: string;
  header?: boolean;
}

export function DocumentListSelectCell({
  checked,
  indeterminate = false,
  label,
  onChange,
  disabledReason,
  header,
}: DocumentListSelectCellProps) {
  const disabled = !onChange || Boolean(disabledReason);
  const input = (
    <input
      type="checkbox"
      className={styles.checkbox}
      checked={checked}
      disabled={disabled}
      aria-label={label}
      onChange={(event) => onChange?.(event.target.checked)}
      ref={(node) => {
        if (node) node.indeterminate = indeterminate && !checked;
      }}
    />
  );
  return (
    <div
      role={header ? "columnheader" : "cell"}
      className={classnames(styles.cell, styles.cellSelect)}
      // Une case désactivée (document hérité) ne doit pas ouvrir l'aperçu de la ligne.
      onClick={(event) => event.stopPropagation()}
    >
      {disabledReason ? (
        <Tooltip content={disabledReason}>{input}</Tooltip>
      ) : (
        input
      )}
    </div>
  );
}

interface DocumentListNameProps {
  icon: ElementType;
  title: ReactNode;
  /** Ligne secondaire sous le titre (nom de fichier, validité…). */
  subtitle?: ReactNode;
  /** Ligne secondaire affichée uniquement sur mobile, où les colonnes date et partage disparaissent. */
  mobileSubtitle?: ReactNode;
  /** Éléments à droite du titre (compteur, chevron). */
  trailing?: ReactNode;
  /** Rend le nom cliquable (ouvrir un dossier, ajouter un document manquant). */
  onClick?: () => void;
  expanded?: boolean;
  controls?: string;
  muted?: boolean;
  className?: string;
}

export function DocumentListName({
  icon: Icon,
  title,
  subtitle,
  mobileSubtitle,
  trailing,
  onClick,
  expanded,
  controls,
  muted,
  className,
}: DocumentListNameProps) {
  const content = (
    <>
      <Icon size={20} className={styles.icon} />
      <div className={styles.text}>
        <Text
          as="div"
          weight="medium"
          color={muted ? "muted" : undefined}
          className={styles.title}
        >
          {title}
        </Text>
        {subtitle && (
          <Text as="span" variant="deci" className={styles.subtitle}>
            {subtitle}
          </Text>
        )}
        {mobileSubtitle && (
          <Text
            as="span"
            variant="deci"
            className={classnames(styles.subtitle, styles.mobileOnly)}
          >
            {mobileSubtitle}
          </Text>
        )}
      </div>
      {trailing}
    </>
  );
  return (
    <div
      role="cell"
      className={classnames(styles.cell, styles.name, className)}
    >
      {onClick ? (
        <BaseButton
          className={styles.nameButton}
          onClick={onClick}
          aria-expanded={expanded}
          aria-controls={controls}
        >
          {content}
        </BaseButton>
      ) : (
        content
      )}
    </div>
  );
}
